import { toSeconds } from '@art-suite/art-core-ts';
import { apiDate, optional, optionalBoolean, optionalNumber, optionalString, optionalUuid, string, uuid, zodLess, ZodLessInfer } from '@kaiizen/zod-less';

// *******************************************************************
// Scan Note API
// *******************************************************************

// NOTE: `editApproval` is deprecated - just use `approval` instead
export const ScanNoteTypeSchema = zodLess('info|editRequest|instructions|editResolution|editApproval|edit|delete|approval');
export type ScanNoteType = ZodLessInfer<typeof ScanNoteTypeSchema>;

/**
 * Who a note is for. `internal` is the project team only: a contractor never sees it, and it never
 * appears on an artifact. `external` is the project team plus the assigned contractor, and appears
 * on artifacts. Contractors can't sign in yet, so today only artifacts enforce the difference.
 *
 * Optional when creating a note. The server stores a visibility on every write: when none is sent,
 * internal for a note Kai wrote (`agentCreated`) and the note type's default otherwise. An edit or
 * delete always gets its parent's, and the server refuses one whose parent it can't find. A note
 * read back with none was saved before the server stored it, and reads as external (see
 * `getScanNoteVisibility`), since those notes have always appeared on artifacts.
 */
export const ScanNoteVisibilitySchema = zodLess('internal|external');
export type ScanNoteVisibility = ZodLessInfer<typeof ScanNoteVisibilitySchema>;

export const ApiScanNoteSchema = zodLess({
  id: uuid,
  createdAt: apiDate,
  userId: uuid,
  scanId: uuid,
  scanPartId: optionalUuid,
  scanPhotoId: optionalUuid,
  regardingNamedValue: optionalString,
  scanNoteType: ScanNoteTypeSchema,
  parentScanNoteId: optionalUuid,
  body: string,
  visibility: optional(ScanNoteVisibilitySchema), // optional on create; see ScanNoteVisibilitySchema

  // provenance for machine-authored notes - false or absent for hand-authored notes
  agentCreated: optionalBoolean, // Kai authored the note (notes are immutable, so createdAt is when)
  scanVideoId: optionalUuid, // parent ScanVideo walkthrough the note came from
  videoFrameTimeSeconds: optionalNumber, // offset in seconds on the parent walkthrough's timeline
});

export type ApiScanNote = ZodLessInfer<typeof ApiScanNoteSchema>;

export const ApiCreateScanNoteSchema = ApiScanNoteSchema.omit({ id: true, createdAt: true }).extend({ id: optionalUuid, createdAt: optional(apiDate) });
export type ApiCreateScanNote = ZodLessInfer<typeof ApiCreateScanNoteSchema>;

/** A note's effective visibility. A note saved before visibility was stored reads as external. */
export const getScanNoteVisibility = (note: Pick<ApiScanNote, 'visibility'>): ScanNoteVisibility => note.visibility ?? 'external';

/** Whether a note may appear on an artifact: anything not marked internal. */
export const isScanNoteExternal = (note: Pick<ApiScanNote, 'visibility'>): boolean => getScanNoteVisibility(note) === 'external';

export type FlattenedApiScanNote = ApiScanNote & {
  history?: ApiScanNote[];
};

/**
 * Resolve all delete and edit note types into a single note with a history of all the notes that were edited.
 * Leaves `scanNotes` as it was.
 *
 * Changes are applied by the note they point at, never by where they fall in time: `createdAt` comes
 * from the writer's device, so a delete or edit can carry an earlier timestamp than its note when
 * that device's clock is behind. A note with any delete is gone; otherwise its latest edit (by
 * `createdAt`) supplies the body. Edits and deletes whose note isn't in the list are ignored.
 * @param scanNotes - The scan notes to flatten
 * @returns The flattened scan notes
 */
export const getFlattenedScanNotes = (scanNotes: readonly ApiScanNote[]): FlattenedApiScanNote[] => {
  const byCreatedAt = (a: ApiScanNote, b: ApiScanNote) => toSeconds(a.createdAt) - toSeconds(b.createdAt);
  // Only edits and deletes change another note; any other note stands on its own.
  const isChange = (note: ApiScanNote) => !!note.parentScanNoteId && (note.scanNoteType === 'edit' || note.scanNoteType === 'delete');

  const deletedNoteIds = new Set<string>();
  const editsByNoteId = new Map<string, ApiScanNote[]>();
  for (const note of scanNotes) {
    if (!isChange(note)) continue;
    const noteId = note.parentScanNoteId as string;
    if (note.scanNoteType === 'delete') deletedNoteIds.add(noteId);
    else editsByNoteId.set(noteId, [...(editsByNoteId.get(noteId) ?? []), note]);
  }

  return [...scanNotes]
    .filter(note => !isChange(note) && !deletedNoteIds.has(note.id))
    .sort(byCreatedAt)
    .map(note => {
      const edits = editsByNoteId.get(note.id)?.sort(byCreatedAt);
      if (!edits) return note;
      const latestEdit = edits[edits.length - 1];
      return {
        ...note,

        // edits can override the body and scanPhotoId fields
        body: latestEdit.body,
        scanPhotoId: latestEdit.scanPhotoId,

        // history contains all instances of the note from the very first to the very last
        history: [note, ...edits],
      };
    });
};
/** How many notes a person sees: edits fold into their notes, and deleted notes aren't counted. */
export const getScanNoteCount = (scanNotes: readonly ApiScanNote[]): number => getFlattenedScanNotes(scanNotes).length;

export const getScanNotesQueryKey = (scanId: string | undefined) => `scanNotes/byScanId/${scanId ?? ''}`;
